import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { contactMethods, offices, quickHelp, faqs, socialMediaLinks } from '../data/contactData';
import ScrollToTop from './common/ScrollToTop';

const Contact = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState(null);

  // Memoize data for performance
  const memoizedContactMethods = useMemo(() => contactMethods, []);
  const memoizedOffices = useMemo(() => offices, []);
  const memoizedQuickHelp = useMemo(() => quickHelp, []);
  const memoizedFaqs = useMemo(() => faqs, []);
  const memoizedSocialMedia = useMemo(() => socialMediaLinks, []);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');

    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', country: '', message: '' });
      setTimeout(() => setFormStatus(null), 5000);
    }, 1000);
  };

  // Handle input change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Scroll progress indicator effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollProgress = document.getElementById('contact-scroll-progress');
      if (scrollProgress && typeof window !== 'undefined') {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight - windowHeight;
        const scrolled = window.scrollY;
        const progress = (scrolled / documentHeight) * 100;
        scrollProgress.style.width = `${Math.min(progress, 100)}%`;
        scrollProgress.setAttribute('aria-valuenow', Math.min(progress, 100).toFixed(0));
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Initial check
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Page scroll progress">
        <div className="h-full bg-gradient-to-r from-teal-500 to-green-500 transition-all duration-300 ease-out" style={{width: '0%'}} id="contact-scroll-progress" aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}></div>
      </div>
      <Helmet>
        <title>Contact Us - Get in Touch | Ravana Migration</title>
        <meta name="description" content="Contact Ravana Migration for expert study abroad consultation. Call +94 11 258 1181, WhatsApp +94 77 123 4567, or email info@ravanaindustries.com. Offices in Sri Lanka, Germany, and Canada." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-teal-50 py-12 sm:py-16">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-40 -right-40 w-80 h-80 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float"></div>
            <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center animate-fade-in-up">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">
                <span className="relative inline-block">
                  <span className="relative z-10">Get in Touch</span>
                  <svg className="absolute -bottom-2 left-0 w-full h-6 text-teal-400 opacity-30" viewBox="0 0 200 20" fill="currentColor">
                    <path d="M0 15 Q 50 5 100 15 T 200 15 L 200 20 L 0 20 Z" />
                  </svg>
                </span>
              </h1>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-xl sm:text-2xl text-gray-700 mb-4 max-w-3xl mx-auto font-medium">
                Connect with our immigration experts today
              </p>
              <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                We're here to help you every step of the way. Choose your preferred method to reach us.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Methods */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Choose your preferred contact method</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 md:gap-8" role="list" aria-label="Contact methods">
            {memoizedContactMethods.map((method) => (
              <a
                key={method.id}
                href={method.link}
                className="card card-hover text-center block hover-lift group"
                role="listitem"
                target={method.id !== 'phone' ? '_blank' : undefined}
                rel={method.id !== 'phone' ? 'noopener noreferrer' : undefined}
              >
                <div className="text-5xl md:text-6xl mb-3 md:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{method.icon}</div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">{method.title}</h3>
                <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">{method.subtitle}</p>
                <p className="text-base md:text-lg font-semibold text-gray-900 mb-2">{method.info}</p>
                <p className="text-xs md:text-sm text-gray-600 mb-4">{method.hours}</p>
                <span className="btn btn-accent inline-flex items-center gap-2">
                  {method.action}
                  {method.id === 'phone' && <span aria-hidden="true">📞</span>}
                  {method.id === 'whatsapp' && <span aria-hidden="true">💬</span>}
                  {method.id === 'email' && <span aria-hidden="true">✉️</span>}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Quick Help */}
        <div className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Quick Help</h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600">Self-service options for faster assistance</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" role="list" aria-label="Quick help options">
              {memoizedQuickHelp.map((help) => (
                <a
                  key={help.id}
                  href={help.link}
                  className="card card-hover text-center hover-lift group"
                  role="listitem"
                  target={help.id === 'status' ? '_blank' : undefined}
                  rel={help.id === 'status' ? 'noopener noreferrer' : undefined}
                >
                  <div className="text-4xl md:text-5xl mb-3 md:mb-4 group-hover:scale-110 transition-transform" aria-hidden="true">{help.icon}</div>
                  <h3 className="text-sm md:text-lg lg:text-xl font-bold text-gray-900 mb-1 md:mb-2 group-hover:text-teal-600 transition-colors">{help.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">{help.description}</p>
                  <span className="btn btn-outline text-xs md:text-sm">
                    {help.linkText}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Office Locations */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Offices</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Visit us or connect with our international team</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" role="list" aria-label="Office locations">
            {memoizedOffices.map((office, index) => (
              <div key={index} className="card card-hover" role="listitem">
                <div className="text-center mb-6">
                  <span className="text-7xl" aria-hidden="true">{office.flag}</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">{office.country}</h3>
                <p className="text-teal-600 text-center mb-4 font-semibold">{office.type}</p>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-semibold text-gray-700">Address:</p>
                    <p className="text-gray-600">{office.address}</p>
                    <p className="text-gray-600">{office.city}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Phone:</p>
                    <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="text-teal-600 hover:underline">
                      {office.phone}
                    </a>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Email:</p>
                    <a href={`mailto:${office.email}`} className="text-teal-600 hover:underline break-all">
                      {office.email}
                    </a>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700">Hours:</p>
                    <p className="text-gray-600">{office.hours}</p>
                  </div>
                  <div className="pt-2 text-center">
                    <a
                      href={index === 0
                        ? `https://maps.google.com/?q=${encodeURIComponent(office.address + ', ' + office.city)}`
                        : `https://maps.google.com/?q=${encodeURIComponent(office.country)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline btn-sm inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View on Map
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* FAQs Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <div className="rule mx-auto mb-4"></div>
            <p className="text-lg sm:text-xl text-gray-600">Quick answers to common questions</p>
          </div>

          <div className="max-w-4xl mx-auto">
            {memoizedFaqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  className="w-full card card-hover text-left p-4 md:p-6 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  aria-expanded={activeFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-base md:text-lg font-bold text-gray-900">{faq.question}</h3>
                    <svg
                      className={`w-5 h-5 md:w-6 md:h-6 text-teal-600 transition-transform ${activeFaq === index ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {activeFaq === index && (
                  <div id={`faq-answer-${index}`} className="card mt-2 p-4 md:p-6 bg-teal-50 border-l-4 border-teal-500">
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Office Information & Directions */}
        <div className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Visit Our Headquarters</h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600">Wellawatta, Colombo 06, Sri Lanka</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              <div className="card card-hover p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  How to Reach Us
                </h3>
                <div className="space-y-4 text-sm md:text-base">
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">By Bus:</p>
                    <p className="text-gray-600">Route 100, 154, 174 - Get off at Wellawatta Bus Station (5 min walk)</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">By Train:</p>
                    <p className="text-gray-600">Wellawatta Railway Station (10 min walk)</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">By Taxi:</p>
                    <p className="text-gray-600">Use PickMe, Uber, or local taxi. Tell driver "Pamankada Lane, Wellawatta"</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Parking:</p>
                    <p className="text-gray-600">Free parking available on-site for clients</p>
                  </div>
                </div>
              </div>

              <div className="card card-hover p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center gap-2">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Accessibility & Facilities
                </h3>
                <div className="space-y-4 text-sm md:text-base">
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Wheelchair Access:</p>
                    <p className="text-gray-600">Full wheelchair accessibility with ramp entrance</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Waiting Area:</p>
                    <p className="text-gray-600">Comfortable seating with free WiFi and refreshments</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Consultation Rooms:</p>
                    <p className="text-gray-600">Private rooms available for confidential consultations</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-700 mb-2">Language Support:</p>
                    <p className="text-gray-600">Consultants available in English, Sinhala, and Tamil</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="bg-gray-50 py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Follow Us on Social Media</h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600">Stay updated with the latest news and tips</p>
            </div>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6" role="list" aria-label="Social media links">
              {memoizedSocialMedia.map((social) => (
                <a
                  key={social.id}
                  href={social.link}
                  className="card card-hover text-center hover-lift group"
                  role="listitem"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visit our ${social.name} page`}
                >
                  <div className="text-4xl md:text-5xl mb-2 md:mb-3 group-hover:scale-110 transition-transform" aria-hidden="true">{social.icon}</div>
                  <p className="text-xs md:text-sm font-bold text-gray-900 mb-1">{social.name}</p>
                  <p className="text-xs text-gray-600 mb-1">{social.handle}</p>
                  <p className="text-xs font-semibold text-teal-600">{social.followers}</p>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Send Us a Message</h2>
              <div className="rule mx-auto mb-4"></div>
              <p className="text-lg sm:text-xl text-gray-600">Fill out the form below and we'll get back to you within 24 hours</p>
            </div>

            <div className="card card-hover p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="+94 XX XXX XXXX"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                      Interested Country
                    </label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select a country</option>
                      <option value="germany">Germany</option>
                      <option value="canada">Canada</option>
                      <option value="australia">Australia</option>
                      <option value="uk">United Kingdom</option>
                      <option value="newzealand">New Zealand</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-vertical"
                    placeholder="Tell us about your study abroad plans..."
                  ></textarea>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold">Message sent successfully!</span>
                    </div>
                    <p className="text-sm mt-1">We'll get back to you within 24 hours.</p>
                  </div>
                )}

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className={`btn btn-accent text-lg px-8 py-3 md:py-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${
                      formStatus === 'sending' ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {formStatus === 'sending' ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            {/* Animated background */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-blue-600 to-indigo-700"></div>
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float"></div>
              <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full mix-blend-overlay filter blur-xl animate-float" style={{animationDelay: '2s'}}></div>
            </div>

            <div className="relative text-center py-12 md:py-16 px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight">
                Ready to Get Started?
              </h2>
              <div className="rule mx-auto mb-4 md:mb-6 bg-white/30"></div>
              <p className="text-lg sm:text-xl text-white mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                Speak with our experts and take the first step towards your dreams
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/choice" className="btn bg-white text-teal-600 hover:bg-gray-100 text-lg px-6 md:px-8 py-3 md:py-4 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group">
                  <span>Start Free Assessment</span>
                  <svg className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <a href="tel:+94112581181" className="btn btn-outline text-white border-white/80 hover:bg-white hover:text-teal-600 text-lg px-6 md:px-8 py-3 md:py-4 backdrop-blur-sm transform hover:scale-105 transition-all duration-300 group">
                  <svg className="w-5 h-5 mr-2 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.113-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6v1z" />
                  </svg>
                  <span>Call Now</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Mobile CTA - respect safe area and chat launcher */}
      <div
        className="fixed left-4 right-24 z-50 md:hidden pb-safe"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + var(--chat-launcher-offset, 80px))' }}
      >
        <a
          href="tel:+94112581181"
          className="btn btn-accent w-full text-center text-lg py-4 shadow-2xl animate-pulse-slow"
          aria-label="Call Ravana Migration now"
        >
          <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.113-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6v1z" />
          </svg>
          Call Now →
        </a>
      </div>

      {/* Scroll to Top */}
      <ScrollToTop />
    </>
  );
};

export default Contact;
